import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";
import Modal from "@/components/Modal";
import type { RedeemedSponsor } from "@/services/api/sponsor/getRedeemedSponsors";
import { openChatRoom } from "@/services/middleware/chatHelper";
import { getActivityStatus, isTaskCompleted } from "@/utils/sponsorStatus";
import { getActivityDeadline } from "@/utils/deadline";

interface StudentRedeemedSponsorCardProps {
  sponsor: RedeemedSponsor;
}

/**
 * Redeemed sponsor card of the sponsor tab (port of the RN `RedeemedSponsor`).
 * Pressing the card opens the sponsor task screen, the PDF strip opens the
 * signed agreement and the trailing button starts a sponsor chat room.
 */
const StudentRedeemedSponsorCard: React.FC<StudentRedeemedSponsorCardProps> = ({
  sponsor,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pdfVisible, setPdfVisible] = useState(false);

  const activity = sponsor.activity;
  const tasks = Array.isArray(activity?.tasks) ? activity.tasks : [];
  const activityDate = activity?.activity_date ?? null;
  const signedAgreement = activity?.signed_agreement ?? "";

  const deadline = (() => {
    const d = getActivityDeadline(activityDate);
    if (!d) return null;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  })();

  const compensationApproved = activity?.compensation_approval_status === "approved";
  const activityStatus = getActivityStatus(tasks, activityDate);
  const isCompleted = compensationApproved || activityStatus.status === "completed";
  const isFailed = activityStatus.status === "failed" && !compensationApproved;

  const completedCount = tasks.filter((task) => isTaskCompleted(task)).length;

  const handleChatWithBusiness = async () => {
    if (!sponsor.user_id) return;
    const room = await openChatRoom({
      receiver_user_id: sponsor.user_id,
      type: "sponsor",
      sponsor_activity_id: activity?.id,
    });
    if (room?.id) navigate(`/student/chat/${room.id}`);
  };

  return (
    <>
      <div className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <button
          type="button"
          onClick={() => {
            if (!activity) return;
            navigate(`/student/sponsor-tasks?sponsorActivityId=${activity.id}`);
          }}
          className="relative block aspect-[1.8/1] w-full bg-gray-100 text-left"
        >
          {sponsor.image_url ? (
            <img
              src={sponsor.image_url}
              alt={sponsor.name ?? ""}
              className="h-full w-full object-cover"
            />
          ) : null}
          {tasks.length > 0 && (
            <span
              className={`absolute right-3 top-6 max-w-[85%] rounded-lg px-2.5 py-1 text-[13px] font-bold shadow-sm ${
                isCompleted
                  ? "bg-[#4CAF50] text-white"
                  : "bg-white text-[#ffa217]"
              }`}
            >
              {isCompleted
                ? `✅ ${t("Sponsor Task Completed")} (${completedCount}/${tasks.length})`
                : `${t("Sponsor Tasks")}: ${completedCount}/${tasks.length} ${t("Completed")}`}
              {!isCompleted && deadline && (
                <span className="mt-0.5 block text-[11px] text-red-500">
                  {`⏰ ${t("Deadline")}: ${deadline}`}
                </span>
              )}
            </span>
          )}
          {(isFailed || isCompleted) && (
            <span
              className={`absolute bottom-0 left-3 flex h-24 w-24 -rotate-2 items-center justify-center rounded-full border-4 border-dashed bg-white text-center text-[13px] font-bold uppercase tracking-wider shadow-md ${
                isFailed ? "border-[#E53935] text-[#E53935]" : "border-[#4CAF50] text-[#4CAF50]"
              }`}
            >
              {isFailed
                ? t("Sponsor Tasks Failed")
                : t("Sponsor Task Completed")}
            </span>
          )}
        </button>

        {activity?.signed_agreement && (
          <button
            type="button"
            onClick={() => setPdfVisible(true)}
            className="mx-4 mt-2.5 mb-1 inline-flex items-center gap-1.5 rounded-lg border border-[#ffa217] px-2.5 py-1.5 text-sm font-bold text-gray-900"
          >
            <span className="text-lg leading-none text-[#E53935]">📄</span>
            {t("View Signed Agreement")}
          </button>
        )}

        <div className="px-4 py-3.5">
          <p className="truncate text-base font-semibold text-gray-900">
            {sponsor.name || t("Room name")}
          </p>
          <p className="mt-0.5 line-clamp-3 text-[13px] text-gray-500">
            {sponsor.description || t("Subtitle")}
          </p>
        </div>

        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={handleChatWithBusiness}
            className="flex w-full items-center justify-center gap-1.5 rounded-[10px] bg-[#ffa217] py-2.5 text-[13px] font-bold text-white"
          >
            <MessageCircle size={18} />
            {t("Chat with Business")}
          </button>
        </div>
      </div>

      <Modal
        visible={pdfVisible}
        onClose={() => setPdfVisible(false)}
        fullScreen
      >
        {/*
          The browser renders the PDF itself rather than through a third-party
          viewer. This used to be `docs.google.com/gview`, which is a page on
          Google's infrastructure that re-fetches the signed agreement on every
          view: it cannot load at all where `docs.google.com` is unreachable, and
          when it cannot fetch the document it answers with a bare "No preview
          available" that the page cannot detect — a dead URL and a live one look
          identical from here. `<object>` points at the Cloudinary asset we
          control and is rendered natively, and per the HTML spec it shows its
          fallback children when the document cannot be instantiated at all and
          fires `error` when it cannot be fetched, so the failure is honest
          without any state to keep in sync.
        */}
        <div className="relative h-full w-full bg-white">
          <object
            data={signedAgreement}
            type="application/pdf"
            aria-label={t("View Signed Agreement")}
            className="h-full w-full"
          >
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#ffa217] px-6 text-center">
              <span className="text-3xl" aria-hidden="true">
                📄
              </span>
              <p className="text-sm font-bold text-white">
                {t("The signed agreement could not be displayed.")}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <a
                  href={signedAgreement}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-white/90 px-3 py-2 text-xs font-bold text-gray-900 shadow"
                >
                  {t("Open the agreement in a new tab")}
                </a>
                <button
                  type="button"
                  onClick={() => setPdfVisible(false)}
                  className="inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-2 text-xs font-bold text-gray-900 shadow"
                >
                  <X size={16} />
                  {t("Close")}
                </button>
              </div>
            </div>
          </object>
          <button
            type="button"
            onClick={() => setPdfVisible(false)}
            className="absolute right-4 bottom-4 inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-2 text-xs font-bold text-gray-900 shadow"
          >
            <X size={16} />
            {t("Close")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default StudentRedeemedSponsorCard;
